import { useEffect, useMemo, useState } from 'preact/hooks';
import { listAllRecordsByType, ApiError, type RecordRow } from '../lib/api';
import { routePath } from '../routes/route';
import { openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { ListSearch, matches } from '../components/ListSearch';
import { ListSort, type SortDir, type SortOption } from '../components/ListSort';
import { activeOnly } from '../lib/retired';
import './Clients.css';

// S4 — THE CLIENTS LIST (wf02 §03, clients()): the registry's front door.
// The header is the honest total.
//
// The COUNT is the full paged truth (listAllRecordsByType — a count built
// on one page would silently lie, the S1 home's own rule). What the
// prototype's rows carry that this port cannot yet: sites-per-client,
// terms, outstanding and open counts need either link-embedded list rows or
// a server aggregate — per-row link reads from here would be an N+1 walk.
// Named gaps, not invented numbers.
//
// -------------------------------------------------------------------
// S12a — THE SLICE IS GONE, AND SO IS THE ⌘K PROMISE.
//
// S4 shipped the prototype's answer to 1,454 clients: show the eight most
// recently touched and say "⌘K to find any other". Both halves failed on
// contact with Huan (2026-08-12): *"I don't even know what order it is in
// now. It tells me to use the knot to search but if I don't know the name
// that isn't as helpful, also the knot doesn't work."*
//
//  1. THE PROMISE WAS NOT KEEPABLE. ⌘K opens the CAPTURE sheet, and the
//     Knot's classifier declares only CREATE targets — asking it to find a
//     client files a note about the client. Huan's ruling: search is its
//     own instrument until the Knot can actually find things. So the line
//     is deleted rather than softened, and nothing on this screen mentions
//     ⌘K. (Binding ⌘K to the box below is a later, separate piece of work,
//     and it is gated on the Knot growing a find intent.)
//
//  2. THE ORDER WAS INVISIBLE. "Most recently touched" is a real rule and
//     it stays the default — the office does think in terms of who they
//     just dealt with — but the ListSort chip now writes it on the screen,
//     and A–Z / Z–A sit beside it.
//
//  3. THE SLICE BECAME A LIE THE MOMENT SEARCH EXISTED. A search that can
//     only reach the first eight rows is worse than no search, so the cap
//     is off: every loaded client is searchable and sortable. A RENDER cap
//     survives (a phone should not paint 1,454 rows at once) — it is far
//     above the demo tenant's 15, it applies AFTER the filter, and when it
//     bites the screen says so with the number it is hiding.

export interface ClientsProps {
  tenantId: string;
  onNavigate: (path: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

const CLIENT_TYPE = 'hvac_client';

/** How many rows this screen will PAINT at once. Not a slice of the truth:
 *  the count above the list is always the full set, search runs over the
 *  full set, and the cap is named on screen the moment it hides anything. */
const RENDER_CAP = 200;

const SORTS: readonly SortOption[] = [
  { key: 'touched', label: 'Recently touched', ascWord: 'oldest', descWord: 'newest', defaultDir: 'desc' },
  { key: 'name', label: 'Name', ascWord: 'A–Z', descWord: 'Z–A', defaultDir: 'asc' },
];

function cmpText(a: string, b: string): number {
  return a.localeCompare(b, 'en-CA', { sensitivity: 'base', numeric: true });
}

function relativeWhen(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const days = Math.round((Date.now() - then) / 86_400_000);
  if (days < 1) return 'today';
  if (days === 1) return '1d ago';
  return `${days}d ago`;
}

function subLineOf(r: RecordRow): string {
  const billing = typeof r.fields['billing_address'] === 'string' ? (r.fields['billing_address'] as string) : '';
  const contact =
    typeof r.fields['principal_contact_name'] === 'string' ? (r.fields['principal_contact_name'] as string) : '';
  return billing || contact || '';
}

export function Clients({ tenantId, onNavigate }: ClientsProps) {
  const [load, setLoad] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [rows, setRows] = useState<RecordRow[]>([]);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('touched');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const recordsVersion = useRecordsVersion();

  useEffect(() => {
    let cancelled = false;
    listAllRecordsByType(tenantId, CLIENT_TYPE)
      .then((fetched) => {
        if (cancelled) return;
        // Most recently touched first — the default order, named by the
        // sort chip below and re-orderable from it (S12a).
        //
        // S11 Part B: a retired client is not in the registry any more — it
        // leaves the list AND the count (core's listRecords excludes only
        // 'deleted', so it used to stay in both). Unlike the Techs roster
        // this screen grows no retired expander: the ruling Huan made was
        // about techs, the design is silent here, and the quieter option is
        // not to add a second affordance. Note this also puts retired
        // clients outside S12a's in-screen search, which filters this same
        // loaded set — reaching one is a direct link or the Knot.
        const sorted = activeOnly(fetched).sort((a, b) =>
          a.updated_at < b.updated_at ? 1 : a.updated_at > b.updated_at ? -1 : 0
        );
        setRows(sorted);
        setLoad('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setErrorMessage(err instanceof ApiError ? err.message : 'Could not reach the server.');
        setLoad('error');
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, recordsVersion]);

  // Search runs over EVERY loaded client, then the sort, then the render
  // cap — in that order, so narrowing can always reach a row the cap would
  // otherwise hide. `rows` arrives most-recently-touched first, which is
  // the 'touched' sort's own descending order.
  const visible = useMemo(() => {
    const found = rows.filter((r) =>
      matches(query, [
        r.display_name,
        typeof r.fields['name'] === 'string' ? (r.fields['name'] as string) : '',
        typeof r.fields['billing_name'] === 'string' ? (r.fields['billing_name'] as string) : '',
        subLineOf(r),
      ])
    );
    const ordered =
      sortKey === 'name'
        ? [...found].sort((a, b) => cmpText(a.display_name, b.display_name))
        : found;
    return sortDir === (sortKey === 'name' ? 'asc' : 'desc') ? ordered : [...ordered].reverse();
  }, [rows, query, sortKey, sortDir]);

  const crumb = (
    <nav class="clients__crumb" aria-label="Breadcrumb">
      <button type="button" onClick={() => onNavigate(routePath({ screen: 'today' }))}>
        Ether Inc
      </button>
      <span class="clients__crumb-sep" aria-hidden="true">
        ›
      </span>
      <span class="clients__crumb-cur">Clients</span>
    </nav>
  );

  if (load === 'loading') {
    return (
      <div class="clients">
        {crumb}
        <div class="clients__eyebrow">Clients</div>
        <div class="clients__say">One moment…</div>
      </div>
    );
  }

  if (load === 'error') {
    return (
      <div class="clients">
        {crumb}
        <div class="clients__eyebrow">Clients</div>
        <div class="clients__say">
          Couldn't load the clients.
          <br />
          <span class="clients__say-soft">{errorMessage}</span>
        </div>
      </div>
    );
  }

  const shown = visible.slice(0, RENDER_CAP);
  const hiddenByCap = visible.length - shown.length;
  const searching = query.trim().length > 0;

  return (
    <div class="clients">
      {crumb}
      <div class="clients__head">
        <div>
          <div class="clients__eyebrow">Clients</div>
          <h2 class="clients__title">
            {rows.length === 1 ? '1 client' : `${rows.length} clients`}
            {searching ? (
              <span class="clients__title-q"> · {visible.length} matching</span>
            ) : null}
          </h2>
        </div>
        <button
          type="button"
          class="clients__add"
          onClick={() => openRecordEditor({ mode: 'create', typeKey: CLIENT_TYPE })}
        >
          + New client
        </button>
      </div>

      {rows.length > 0 ? (
        <>
          <ListSearch
            value={query}
            onChange={setQuery}
            label="Search clients by name"
            placeholder="Search clients"
          />
          <ListSort
            options={SORTS}
            activeKey={sortKey}
            dir={sortDir}
            onChange={(key, dir) => {
              setSortKey(key);
              setSortDir(dir);
            }}
          />
        </>
      ) : null}

      {hiddenByCap > 0 ? (
        <div class="clients__sub">
          Showing the first {shown.length} in this order. {hiddenByCap} more are loaded and
          searchable — narrow the search to reach them.
        </div>
      ) : null}

      {rows.length === 0 ? (
        <div class="clients__empty">No clients yet.</div>
      ) : visible.length === 0 ? (
        <div class="clients__empty">No client matches “{query.trim()}”.</div>
      ) : (
        <div class="clients__list">
          {shown.map((r) => (
            <button
              type="button"
              class="clients__row"
              key={r.id}
              onClick={() => onNavigate(routePath({ screen: 'clientDetail', id: r.id }))}
            >
              <span class="clients__stripe" />
              <span class="clients__main">
                <b>{r.display_name}</b>
                {subLineOf(r) ? <small>{subLineOf(r)}</small> : null}
              </span>
              <span class="clients__right">
                {relativeWhen(r.updated_at)}
                <span class="clients__st">touched</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
